import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, MapPin, ChevronRight, Bell, Heart } from 'lucide-react';
import { DataService } from '../services/dataService';
import { SiteConfig, Banner } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { DEFAULT_BANNERS } from '../constants';

const Home: React.FC = () => {
  const [config, setConfig] = useState<SiteConfig>(DataService.getConfig());
  const [banners, setBanners] = useState<Banner[]>(DataService.getBanners());
  const { language, t } = useLanguage();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isMusicPlaying, setIsMusicPlaying] = useState(true);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);

  useEffect(() => {
    const handler = (e: any) => {
      try {
        const db = e?.detail?.db || DataService.getDB();
        if (db) {
          setConfig(db.config || DataService.getConfig());
          setBanners(db.banners || DataService.getBanners());
        }
      } catch (err) {
        console.warn('Home db_updated handler error', err);
      }
    };

    window.addEventListener('db_updated', handler as EventListener);

    // initial sync
    try {
      const db = DataService.getDB();
      if (db) {
        setConfig(db.config || DataService.getConfig());
        setBanners(db.banners || DataService.getBanners());
      }
    } catch (err) {
      console.warn('Home initial DB load error', err);
    }

    return () => window.removeEventListener('db_updated', handler as EventListener);
  }, []);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.loop = true;
    const vol = typeof config.homeMediaVolume === 'number' ? Math.max(0, Math.min(1, config.homeMediaVolume)) : 0.5;
    a.volume = vol;
    if (isMusicPlaying) {
      const p = a.play();
      if (p && typeof (p as any).catch === 'function') {
        (p as Promise<void>).catch(() => {
          setAutoplayBlocked(true);
          setIsMusicPlaying(false);
        });
      }
    } else {
      a.pause();
    }
  }, [isMusicPlaying, config.homeMediaVolume]);

  useEffect(() => {
    if (!autoplayBlocked) return;
    const handler = () => {
      const a = audioRef.current;
      if (!a) return;
      a.play().then(() => {
        setIsMusicPlaying(true);
        setAutoplayBlocked(false);
        document.removeEventListener('click', handler);
        document.removeEventListener('touchstart', handler);
      }).catch(() => {});
    };
    document.addEventListener('click', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('click', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [autoplayBlocked]);

  // ... keep the JSX from the original file below ...

// redundant localStorage fallback removed

  //const { language, t } = useLanguage();

  useEffect(() => {
    setConfig(DataService.getConfig());
    const currentBanners = DataService.getBanners();
    if (currentBanners && (currentBanners?.length ?? 0) > 0) {
        setBanners(currentBanners);
    }
  }, []);

  // Use the first banner as Hero
  const heroBanner = (banners?.length ?? 0) > 0 ? banners[0] : DEFAULT_BANNERS[0];

  const getTranslatedAnnouncement = (index: number) => {
    if (language === 'ml' && config.announcementsMal && config.announcementsMal[index]) {
      return config.announcementsMal[index];
    }
    return config.announcements[index];
  };

  return (
    <div className="flex flex-col">
      <audio ref={audioRef} src="/images/amme.mp3" preload="auto" className="w-0 h-0 absolute -z-10" type="audio/mpeg" />
      {/* Hero Section */}
      <div className="relative h-[60vh] md:h-[80vh] w-full overflow-hidden bg-gray-900">
        <img 
          src={heroBanner.imageUrl} 
          onError={(e) => {e.currentTarget.src = 'https://via.placeholder.com/1600x900?text=Temple+Hero+Image'}}
          alt="Arunoottimangalam Bhagavathy Temple" 
          className="absolute inset-0 w-full h-full object-cover opacity-80 animate-hero-strike will-change-transform"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-temple-orange-900/80 via-transparent to-transparent animate-hero-glow"></div>
        <div className="relative h-full container mx-auto px-4 flex flex-col justify-center items-center text-center text-white z-10 pt-20">
          <h2 className="text-xl md:text-3xl font-light mb-4 text-orange-100 animate-fade-in-up drop-shadow-md">
            {t('Welcome to', 'സ്വാഗതം')}
          </h2>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif font-bold mb-6 drop-shadow-lg leading-tight">
            {language === 'en' ? (heroBanner.title || config.templeName) : (heroBanner.titleMal || config.templeNameMal || "അറുനൂറ്റിമംഗലം ഭഗവതി ക്ഷേത്രം")}
          </h1>
          <p className="text-lg md:text-2xl max-w-2xl text-orange-50 mb-8 font-light drop-shadow-md">
             {/* Dynamic Tagline based on Language */}
             {language === 'en' ? heroBanner.subtitle : (heroBanner.subtitleMal || heroBanner.subtitle)}
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/poojas" className="bg-temple-orange-500 hover:bg-temple-orange-600 text-white px-8 py-3 rounded-full font-bold text-lg transition-all transform hover:scale-105 shadow-lg shadow-orange-600/50">
              {t('Book Pooja', 'പൂജ ബുക്ക് ചെയ്യൂ')}
            </Link>
            <Link to="/donate" className="bg-white text-temple-orange-800 hover:bg-orange-50 px-8 py-3 rounded-full font-bold text-lg transition-all shadow-lg flex items-center justify-center gap-2">
              <Heart size={20} className="fill-current" /> {t('Donate', 'സംഭാവന')}
            </Link>
          </div>
        <div className="mt-2 sm:mt-6 -translate-y-2 sm:translate-y-0">
           <button
              onClick={() => setIsMusicPlaying((p) => !p)}
              className="bg-white/90 text-temple-orange-800 px-4 py-2 rounded-full font-bold hover:bg-white transition-colors shadow"
            >
              {isMusicPlaying ? t('Stop Music', 'സംഗീതം നിർത്തുക') : t('Play Music', 'സംഗീതം പ്ലേ ചെയ്യുക')}
            </button>
          </div> 
        </div>
      </div>

      {/* Announcements Ticker */}
      {(config.announcements?.length ?? 0) > 0 && (
        <div className="bg-temple-orange-100 border-b border-orange-200 py-3 overflow-hidden relative">
          <div className="container mx-auto px-4 flex items-center">
            <span className="bg-temple-orange-500 text-white text-xs font-bold px-2 py-1 rounded mr-3 uppercase tracking-wider flex items-center gap-1 shrink-0">
              <Bell size={12} /> {t('News', 'അറിയിപ്പുകൾ')}
            </span>
            <div className="flex-1 overflow-hidden whitespace-nowrap">
              <div className="inline-block animate-marquee pl-full">
                {(config.announcements ?? []).map((ann, idx) => (
                  <span key={idx} className="mr-12 text-temple-orange-900 font-bold">
                    {getTranslatedAnnouncement(idx)}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Darshan Timings */}
      <section style={{ marginBottom: '5px', marginTop: '5px'  }} className="py-12 bg-white shadow-sm relative z-20 -mt-8 mx-4 md:mx-auto md:max-w-4xl rounded-xl border border-orange-100">
        <div className="grid md:grid-cols-3 gap-8 divide-y md:divide-y-0 md:divide-x divide-orange-100 px-6">
          <div className="flex flex-col items-center text-center p-2">
            <Clock className="text-temple-orange-500 mb-3 w-8 h-8" />
            <h3 className="font-serif font-bold text-xl text-gray-800 mb-2">{t('Morning Darshan', 'രാവിലെ ദർശനം')}</h3>
            <p className="text-gray-600">5:30 AM - 10:00 AM</p>
              <p className="text-sm text-gray-500 mt-1">{t('Pooja at 09:00 AM', 'പൂജ  09:00-ന്')}</p>
           
          </div>
          <div className="flex flex-col items-center text-center p-2">
            <Clock className="text-temple-orange-500 mb-3 w-8 h-8" />
            <h3 className="font-serif font-bold text-xl text-gray-800 mb-2">{t('Evening Darshan', 'വൈകുന്നേര ദർശനം')}</h3>
            <p className="text-gray-600">5:30 PM - 7:00 PM</p>
            <p className="text-sm text-gray-500 mt-1">{t('Deeparadhana at 6:30 PM', 'ദീപാരാധന 6:30-ന്')}</p>
          </div>
          <div className="flex flex-col items-center text-center p-2">
            <Calendar className="text-temple-orange-500 mb-3 w-8 h-8" />
            <h3 className="font-serif font-bold text-xl text-gray-800 mb-2">{t('Pooja Days', 'പൂജ ദിവസങ്ങൾ ')}</h3>
            <p className="text-gray-600">{t('Every Malayalam month 31 ,1 dates', 'എല്ലാ മലയാളം മാസവും 31 ,1 തീയതികൾ ')}</p>
         
          </div>
        </div>
      </section>
  

      {/* About Section Snippet */}
      <section className="py-20 px-4 bg-temple-cream">
        <div className="container mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div className="order-2 md:order-1">
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-temple-orange-400 to-temple-gold rounded-lg blur opacity-25 group-hover:opacity-75 transition duration-1000 group-hover:duration-200"></div>
              <img 
                src="/images/diety.png" 
                onError={(e) => {e.currentTarget.src = 'https://via.placeholder.com/600x800?text=About+Image'}}
                alt="Deity" 
                className="relative rounded-g shadow-xl border-5 border-white animate-fade-in transition-transform duration-500 group-hover:scale-105"
              />
              
            </div>
          </div>
          <div className="order-1 md:order-2">
            <h2 className="text-temple-orange-600 font-bold uppercase tracking-wider text-sm mb-2">{t('About The Temple', 'ക്ഷേത്രത്തെക്കുറിച്ച്')}</h2>
            
            {/* Dynamic Title */}
            <h2 className="text-4xl font-serif font-bold text-temple-orange-900 mb-6">
              {language === 'en' 
                ? (config.homeAboutTitle || "A Sacred Hindu Temple") 
                : (config.homeAboutTitleMal || "പുണ്യമായ ഹൈന്ദവ ക്ഷേത്രം ")
              }
            </h2>
            
            {/* Dynamic Text */}
            <p className="text-gray-700 leading-relaxed mb-6 text-lg">
              {language === 'en'
                ? (config.homeAboutText || "Arunoottimangalam Bhagavathy Temple is an ancient Hindu temple...")
                : (config.homeAboutTextMal || "കേരളത്തിൽ സ്ഥിതി ചെയ്യുന്ന പുരാതനമായ ക്ഷേത്രമാണ് അറുനൂറ്റിമംഗലം  ഭഗവതി ക്ഷേത്രം...")
              }
            </p>
            
          
            <Link to="/about" className="text-temple-orange-700 font-bold flex items-center gap-2 hover:gap-3 transition-all">
              {t('More', 'കൂടുതൽ')} <ChevronRight size={20} />
            </Link>
          </div>
        </div>
      </section>
      {/* Other Deities */}
      <section className="py-16 bg-temple-cream">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-temple-orange-900 mb-8">
            {t('Other Deities', 'ഉപദേവതകൾ')}
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {  img: '/images/krishna.png' },
              {  img: '/images/ganapathy.png' },
              { img: '/images/sarpa-2.jpg' },
            ].map((d, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-orange-100 shadow-sm text-center group">
                <img
                  src={d.img}                 
                  className="w-40 h-40 mx-auto rounded-full object-cover border-4 border-white shadow animate-fade-in transition-transform duration-500 group-hover:scale-105"
                />               
               
              </div>
            ))}

          
          </div>
        </div>
      </section>
      {/* Priests */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-temple-orange-900 mb-8">
            {t('Temple Priests', 'ക്ഷേത്ര പുരോഹിതർ')}
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-orange-50 rounded-xl p-6 border border-orange-100 flex items-center gap-6 group">
              <img
                src={config.thantriImageUrl || '/images/placeholder-person.jpg'}
                onError={(e) => { e.currentTarget.src = 'https://via.placeholder.com/150?text=Thantri'; }}
                alt="Thantri"
                className="w-24 h-24 rounded-full object-cover border-2 border-white shadow animate-fade-in transition-transform duration-500 group-hover:scale-105"
              />
              <div>
                <p className="text-sm text-temple-orange-700 font-bold uppercase tracking-wide">{t('Thantri', 'തന്ത്രി')}</p>
                <h3 className="text-xl font-bold text-gray-800">{config.thantriName || t('Not set', 'ക്രമീകരിച്ചിട്ടില്ല')}</h3>
              </div>
            </div>
            <div className="bg-orange-50 rounded-xl p-6 border border-orange-100 flex items-center gap-6 group">
              <img
                src={config.pujariImageUrl || '/images/placeholder-person.jpg'}
                onError={(e) => { e.currentTarget.src = 'https://via.placeholder.com/150?text=Pujari'; }}
                alt="Pujari"
                className="w-24 h-24 rounded-full object-cover border-2 border-white shadow animate-fade-in transition-transform duration-500 group-hover:scale-105"
              />
              <div>
                <p className="text-sm text-temple-orange-700 font-bold uppercase tracking-wide">{t('Pujari', 'പൂജാരി')}</p>
                <h3 className="text-xl font-bold text-gray-800">{config.pujariName || t('Not set', 'ക്രമീകരിച്ചിട്ടില്ല')}</h3>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Quick Donation / Offerings Preview */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-temple-orange-900 mb-4">{t('Divine Offerings', 'വഴിപാടുകൾ')}</h2>
          <p className="text-gray-600 max-w-2xl mx-auto mb-12">
            {t('Participate in the daily rituals and seek the blessings of the Goddess through our various sevas.', 'വിവിധ സേവകളിലൂടെ നിത്യപൂജകളിൽ പങ്കെടുത്ത് ദേവിയുടെ അനുഗ്രഹം നേടുക.')}
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
             {[
               { 
                 title: t("Ganapathy Homam", "ഗണപതി ഹോമം"),                
                 desc: t("For removing obstacles", "വിഘ്നങ്ങൾ നീക്കാൻ"), 
                 icon: "🌸", 
                 link: "/poojas" 
               },
               { 
                 title: t("Bhagaavthy Seva", "ഭഗവതി സേവ"),                 
                 desc: t("For general well-being", "പൊതുവായ ക്ഷേമത്തിന്"), 
                 icon: "🔥", 
                 link: "/poojas" 
               },
               { 
                 title: t("Saarpa Pooja", "സർപ്പപൂജ"),                  
                 desc: t("To remove snake dosha", "സർപ്പദോഷം അകറ്റാൻ "), 
                 icon: "🍚", 
                 link: "/poojas" 
               },
             ].map((item, idx) => (
               <div key={idx} className="bg-orange-50 p-8 rounded-xl hover:shadow-lg transition-shadow border border-orange-100 group">
                 <div className="text-4xl mb-4 group-hover:scale-110 transition-transform">{item.icon}</div>
                 <h3 className="text-xl font-bold text-gray-800 mb-2">{item.title}</h3>
                 <p className="text-gray-600 mb-4">{item.desc}</p>               
                 <Link to={item.link} className="inline-block border-2 border-temple-orange-600 text-temple-orange-600 px-6 py-2 rounded-full font-bold hover:bg-temple-orange-600 hover:text-white transition-colors">
                   {item.link === '/donate' ? t('Donate Now', 'സംഭാവന നൽകുക') : t('Book Now', 'ബുക്ക് ചെയ്യുക')}
                 </Link>
                 
               </div>
             ))}
          </div>
           
        </div>
      </section>
      
      {/* Map Section */}
      <section className="h-96 w-full bg-gray-200 relative">
        <iframe 
          title="Temple Location"
        src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d3931.590070719745!2d76.507287!3d9.80071!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3b07d7796e9c390b%3A0x7a7e27cd09dd39ac!2sArunoottimangalam%20bhagavathy%20temple!5e0!3m2!1sen!2sin!4v1765951324929!5m2!1sen!2sin"    width="100%" 
          height="100%" 
          style={{ border: 0 }} 
          loading="lazy"
        ></iframe>
        <div className="absolute bottom-4 left-4 bg-white p-4 rounded shadow-lg max-w-xs border border-orange-200">
          <h4 className="font-bold text-temple-orange-800 flex items-center gap-2"><MapPin size={16} className="text-red-500"/> {t('Visit Us', 'സന്ദർശിക്കുക')}</h4>
          <p className="text-sm text-gray-600 mt-1">{language === 'en' ? config.address : (config.addressMal || config.address)}</p>
        </div>
      </section>
    </div>
  );
};

export default Home;
